import React, { useEffect, useState } from "react";

import axios from "axios";
import { END_POINT } from "../../constants/defaultValues";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import AffiliateLayout from "./layout";

// Timeline Step Component
const TimelineStep = ({ label, date, isCompleted, isActive, isLast }) => (
  <div
    className={`timeline-step ${isCompleted ? "completed" : ""} ${
      isActive ? "active" : ""
    }`}
  >
    <div className="step-indicator">
      <div className="step-dot">
        {isCompleted && (
          <svg
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            width="14"
            height="14"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>
      {!isLast && <div className="step-line"></div>}
    </div>
    <div className="step-content">
      <span className="step-label">{label}</span>
      <span className="step-date">{date || "—"}</span>
    </div>
  </div>
);

const Customers = ({ history }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem("affiliate_token");
      if (!token) {
        history.push("/login");
        return;
      }

      const res = await axios.get(`${END_POINT}/api/affiliate/customers`, {
        headers: { "x-auth-token": token },
      });

      if (res.status === 200) {
        setCustomers(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError("Không thể tải danh sách khách hàng");
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = (customer) => {
    setSelectedCustomer(customer);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedCustomer(null);
  };

  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Determine timeline status based on customer data
  const getTimelineData = (customer) => {
    const hasRegistered = !!customer.createdAt;
    const hasTrial = customer.plan === "free" || customer.plan === "trial";
    const hasPaid = customer.ltv > 0 || customer.upgraded;
    const isActive = customer.status === "active";

    return [
      {
        label: "Click",
        date: customer.clickedAt
          ? formatDate(customer.clickedAt)
          : "Không có dữ liệu",
        isCompleted: true,
        isActive: false,
      },
      {
        label: "Đăng ký",
        date: formatDate(customer.createdAt),
        isCompleted: hasRegistered,
        isActive: hasRegistered && !hasPaid,
      },
      {
        label: "Dùng thử",
        date:
          hasTrial && !hasPaid
            ? "Đang dùng thử"
            : hasPaid
            ? "Đã hoàn thành"
            : "Chưa bắt đầu",
        isCompleted: hasTrial || hasPaid,
        isActive: hasTrial && !hasPaid,
      },
      {
        label: "Thanh toán",
        date: hasPaid
          ? `${new Intl.NumberFormat("vi-VN").format(customer.ltv || 0)} VNĐ`
          : "Chưa thanh toán",
        isCompleted: hasPaid,
        isActive: hasPaid && isActive,
      },
      {
        label: isActive ? "Gia hạn" : "Hết hạn",
        date: isActive ? "Đang hoạt động" : "Đã hết hạn",
        isCompleted: false,
        isActive: !isActive && hasPaid,
      },
    ];
  };

  return (
    <AffiliateLayout title="Khách hàng">
      <div className="aff-page-container">
        <div className="page-header">
          <h2>Danh sách khách hàng</h2>
          <p>Quản lý và thống kê khách hàng đã đăng ký qua liên kết của bạn.</p>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="simple-spinner"></div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : (
          <div className="aff-card">
            <div className="card-header">
              <h3>Tất cả khách hàng</h3>
              <div className="header-actions">
                <span className="count-badge">
                  Tổng: {customers.length} khách hàng
                </span>
              </div>
            </div>

            <div className="aff-table-container">
              <table className="aff-table">
                <thead>
                  <tr>
                    <th style={{ width: "22%" }}>Khách hàng</th>
                    <th style={{ width: "16%" }}>Email</th>
                    <th className="text-center" style={{ width: "10%" }}>
                      Gói hiện tại
                    </th>
                    <th className="text-center" style={{ width: "12%" }}>
                      Trạng thái
                    </th>
                    <th className="text-right" style={{ width: "12%" }}>
                      Tổng chi tiêu
                    </th>
                    <th className="text-right" style={{ width: "10%" }}>
                      Hoa hồng
                    </th>
                    <th className="text-right" style={{ width: "10%" }}>
                      Ngày ĐK
                    </th>
                    <th className="text-center" style={{ width: "8%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length > 0 ? (
                    customers.map((item, index) => {
                      const user = item.user || {};

                      return (
                        <tr key={index}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div
                                className="avatar-circle mr-3"
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  backgroundColor: "#e2e8f0",
                                  color: "#64748b",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "600",
                                  fontSize: "14px",
                                }}
                              >
                                {user.name
                                  ? user.name.charAt(0).toUpperCase()
                                  : "U"}
                              </div>
                              <div>
                                <div className="font-weight-bold text-dark">
                                  {user.name || "N/A"}
                                </div>
                                <div className="text-muted small">
                                  ID: {item.user?.id || "N/A"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="text-muted">{user.email || "N/A"}</td>
                          <td className="text-center">
                            <span
                              className={`status-badge ${
                                item.upgraded ? "paid" : "pending"
                              }`}
                            >
                              {item.upgraded
                                ? "LIFETIME"
                                : (item.plan || "free").toUpperCase()}
                            </span>
                          </td>
                          <td className="text-center">
                            <span
                              className={`status-badge ${
                                item.status === "active"
                                  ? "approved"
                                  : "rejected"
                              }`}
                            >
                              {item.status === "active"
                                ? "Đang hoạt động"
                                : "Hết hạn"}
                            </span>
                          </td>
                          <td className="text-right font-weight-bold">
                            {new Intl.NumberFormat("vi-VN", {
                              style: "currency",
                              currency: "VND",
                            }).format(item.ltv || 0)}
                          </td>
                          <td className="text-right text-success font-weight-bold">
                            {new Intl.NumberFormat("vi-VN", {
                              style: "currency",
                              currency: "VND",
                            }).format(item.total_commission || 0)}
                          </td>
                          <td className="text-right text-muted small">
                            {new Date(item.createdAt).toLocaleDateString(
                              "vi-VN"
                            )}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn-detail"
                              onClick={() => openDetailModal(item)}
                            >
                              Chi tiết
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center empty-state p-5">
                        <div className="text-muted mb-2">
                          <i
                            className="simple-icon-people"
                            style={{ fontSize: "32px" }}
                          />
                        </div>
                        <p>Chưa có khách hàng nào được giới thiệu.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Customer Detail Modal */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="md" centered>
        <ModalHeader toggle={closeModal}>
          <div className="d-flex align-items-center">
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#10b981",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "600",
                fontSize: "16px",
                marginRight: "12px",
              }}
            >
              {selectedCustomer?.user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>
                {selectedCustomer?.user?.name || "Khách hàng"}
              </div>
              <div
                style={{ fontSize: "13px", color: "#64748b", fontWeight: 400 }}
              >
                {selectedCustomer?.user?.email || ""}
              </div>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          {selectedCustomer && (
            <div className="customer-detail-content">
              {/* Summary Stats */}
              <div className="detail-stats-grid">
                <div className="detail-stat">
                  <span className="stat-label">Tổng chi tiêu</span>
                  <span className="stat-value">
                    {new Intl.NumberFormat("vi-VN").format(
                      selectedCustomer.ltv || 0
                    )}{" "}
                    VNĐ
                  </span>
                </div>
                <div className="detail-stat">
                  <span className="stat-label">Hoa hồng nhận được</span>
                  <span className="stat-value text-success">
                    {new Intl.NumberFormat("vi-VN").format(
                      selectedCustomer.total_commission || 0
                    )}{" "}
                    VNĐ
                  </span>
                </div>
              </div>

              {/* Timeline */}
              <div className="timeline-section">
                <h4>Hành trình khách hàng</h4>
                <div className="customer-timeline">
                  {getTimelineData(selectedCustomer).map((step, idx, arr) => (
                    <TimelineStep
                      key={idx}
                      label={step.label}
                      date={step.date}
                      isCompleted={step.isCompleted}
                      isActive={step.isActive}
                      isLast={idx === arr.length - 1}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </AffiliateLayout>
  );
};

export default Customers;
